// Tenant context for every cart/order request: account, channel, location,
// and the active currency. Seeded from the industry config (with .env overrides
// for local dev) and persisted so reload doesn't lose location selection.
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORE_CONTEXT_STORAGE_KEY } from '@core/constants/auth';
import { CUSTOMER_TYPE, orderSourceFor, platformSlugFor } from '@core/constants/platform';

/** @typedef {import('@core/types').CurrencyConfig} CurrencyConfig */

const empty = {
  accountId: '',
  channelId: '',
  locationId: '',
  brandId: '',
  brandName: '',
  companyId: '',
  companyName: '',
  accountCountry: '',
  customerLocation: null,
  // BUSINESS only when the selected location sells on the B2B platform and the
  // shopper picked "Business"; drives platform_slug on every catalog/cart call.
  customerType: CUSTOMER_TYPE.INDIVIDUAL,
  // account_platform_id of the location's ecommerce-b2b platform. Replaces
  // channelId for business shoppers; kept apart because bootstrap re-seeds
  // channelId from the resolve call on every load.
  b2bChannelId: '',
  isRuntimeSwitching: false,
  priceField: 'online_price',
  currencyConfig: /** @type {CurrencyConfig | null} */ (null),
};

export const useStoreContextStore = create(
  persist(
    (set) => ({
      ...empty,

      hydrate: (next) => set((s) => ({ ...s, ...next })),
      setLocation: (locationId) => set({ locationId }),
      setBrand: (brandId, brandName = '', companyName = '', companyId = '') =>
        set({ brandId, brandName, companyName, companyId }),
      setRuntimeSwitching: (isRuntimeSwitching) =>
        set({ isRuntimeSwitching: Boolean(isRuntimeSwitching) }),
      setCustomerLocation: (customerLocation) => set({ customerLocation }),
      setCustomerType: (customerType, { b2bChannelId = '' } = {}) => {
        const isBusiness = customerType === CUSTOMER_TYPE.BUSINESS;
        set({
          customerType: isBusiness ? CUSTOMER_TYPE.BUSINESS : CUSTOMER_TYPE.INDIVIDUAL,
          b2bChannelId: isBusiness ? String(b2bChannelId || '').trim() : '',
        });
      },
      setAccountCountry: (accountCountry) => set({ accountCountry }),
      setChannel: (channelId) => set({ channelId }),
      setCurrency: (currencyConfig) => set({ currencyConfig }),
      reset: () => set({ ...empty }),
    }),
    {
      name: STORE_CONTEXT_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accountId: state.accountId,
        channelId: state.channelId,
        locationId: state.locationId,
        brandId: state.brandId,
        brandName: state.brandName,
        companyId: state.companyId,
        companyName: state.companyName,
        accountCountry: state.accountCountry,
        customerLocation: state.customerLocation,
        customerType: state.customerType,
        b2bChannelId: state.b2bChannelId,
        priceField: state.priceField,
        currencyConfig: state.currencyConfig,
      }),
    },
  ),
);

export const selectIsBusiness = (state) => state.customerType === CUSTOMER_TYPE.BUSINESS;
export const selectPlatformSlug = (state) => platformSlugFor(state.customerType);
export const selectOrderSource = (state) => orderSourceFor(state.customerType);
/** Channel for every channel-scoped call: the B2B platform's id for business shoppers. */
export const selectActiveChannelId = (state) =>
  (selectIsBusiness(state) && state.b2bChannelId) || state.channelId;

/** platform_slug for the current shopper — `ecommerce` or `ecommerce-b2b`. */
export const getPlatformSlug = () => selectPlatformSlug(useStoreContextStore.getState());
/** cart/order `source` for the current shopper — `ecommerce` or `ecommerce-b2b`. */
export const getOrderSource = () => selectOrderSource(useStoreContextStore.getState());
export const getActiveChannelId = () => selectActiveChannelId(useStoreContextStore.getState());
